import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { Contract } from '../../../types/contract';
import ContractBasicInfoForm from './ContractBasicInfoForm';
import { ContractTemplateSubPageContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const EditContractPage = () => {
  const { updateContract } = useIotsApi();

  const {
    contract,
    error,
    isFetching,
    updateContextContract,
    setDisplayedContractName,
    isContractDisabled,
  } = useOutletContext<ContractTemplateSubPageContext>();

  const { t } = useLanguage();

  const updateContractMutation = useMutation({
    mutationFn: updateContract,
  });

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const isLoading = isFetching || updateContractMutation.isPending;

  return (
    <ContractBasicInfoForm
      contract={contract}
      disabled={Boolean(error || isContractDisabled)}
      isLoading={isLoading}
      onContractNameChange={setDisplayedContractName}
      onSubmit={(values, callback) => {
        const updatedContract: Contract = {
          ...contract,
          id: contract.id,
          name: values.name,
          operatorId: values.operatorId,
          dateFrom: values.startDate,
          dateTo: values.endDate,
          serviceType: values.serviceType,
          allowedPorts: values.allowedPortsIds,
          defaultPortOfDepartureId: values.defaultDeparturePortId,
          defaultPortOfArrivalId: values.defaultArrivalPortId,
          county: values.county,
        };

        updateContractMutation.mutate(updatedContract, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.contract-update-success'),
            });
            // A successful update will just update the item cached data
            updateContextContract(updatedContract);
            callback();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(
                error,
                `${t('content.contract-update-error')}`,
              ),
            });
            updateContractMutation.reset();
          },
        });
      }}
    />
  );
};

export default EditContractPage;
